import { useState, useRef, useEffect } from "react";
import { API } from "@/lib/api";
import { Heart, X, MessageCircle, Volume2, VolumeX } from "lucide-react";
import { ChatMessages, VoiceStatus, ChatComposer } from "@/components/AiChatParts";
import { useVoiceInput, useSpeaker } from "@/lib/voice";
import { useNavigate } from "react-router-dom";
import { matchIntent } from "@/lib/intents";

export default function AiAssistant() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: 0, role: "assistant", content: "Hello, I'm your Kindred Companion. I can help you post a request, find a trusted helper, or answer any questions. How can I help you today?" },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [readAloud, setReadAloud] = useState(() => localStorage.getItem("kindred_read_aloud") === "1");
  const sessionRef = useRef(null);
  const endRef = useRef(null);
  const { speaking, speak, stop: stopSpeaking } = useSpeaker();
  const voice = useVoiceInput((text) => send(text, true));

  const toggleReadAloud = () => {
    const v = !readAloud;
    setReadAloud(v);
    localStorage.setItem("kindred_read_aloud", v ? "1" : "0");
    if (!v) stopSpeaking();
  };

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (override, fromVoice = false) => {
    const text = (override ?? input).trim();
    if (!text || busy) return;
    setInput("");
    const intent = matchIntent(text);
    if (intent) {
      setMessages((m) => [...m, { id: m.length, role: "user", content: text }, { id: m.length + 1, role: "assistant", content: intent.say }]);
      if (readAloud || fromVoice) speak(intent.say);
      if (intent.to) { navigate(intent.to); setOpen(false); }
      if (intent.action === "sos") { setOpen(false); document.querySelector("[data-testid=sos-button]")?.click(); }
      return;
    }
    setMessages((m) => [...m, { id: m.length, role: "user", content: text }, { id: m.length + 1, role: "assistant", content: "" }]);
    setBusy(true);
    let acc = "";
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("kindred_token")}`,
        },
        body: JSON.stringify({ message: text, session_id: sessionRef.current }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        let chunk = decoder.decode(value, { stream: true });
        const match = chunk.match(/^__SESSION__(.+?)__\n/);
        if (match) {
          sessionRef.current = match[1];
          chunk = chunk.replace(match[0], "");
        }
        acc += chunk;
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { ...copy[copy.length - 1], content: acc };
          return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { ...copy[copy.length - 1], content: "Sorry, I couldn't respond. Please try again." };
        return copy;
      });
    } finally {
      setBusy(false);
      if (acc && (readAloud || fromVoice)) speak(acc);
    }
  };

  return (
    <>
      {!open && (
        <button
          data-testid="ai-assistant-toggle"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 bg-[#2E7D6B] hover:bg-[#256658] text-white rounded-full shadow-xl px-5 py-4 flex items-center gap-2 transition-all hover:scale-105"
        >
          <MessageCircle className="w-6 h-6" />
          <span className="font-semibold hidden sm:inline">Companion</span>
        </button>
      )}

      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[calc(100vw-3rem)] sm:w-[400px] h-[560px] max-h-[80vh] bg-white rounded-2xl shadow-2xl border border-[#E2D9CE] flex flex-col overflow-hidden k-fade-in">
          <div className="bg-[#2E7D6B] text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                <Heart className="w-5 h-5" fill="white" />
              </span>
              <div>
                <p className="font-semibold leading-tight">Kindred Companion</p>
                <p className="text-xs text-white/80">Here to help, anytime</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button data-testid="ai-read-aloud-toggle" onClick={toggleReadAloud} title={readAloud ? "Turn off read aloud" : "Read replies aloud"}
                className={`p-2 rounded-lg min-w-[40px] min-h-[40px] flex items-center justify-center ${readAloud ? "bg-white/25" : "hover:bg-white/20"}`}>
                {readAloud ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
              </button>
              <button data-testid="ai-close-btn" onClick={() => { stopSpeaking(); setOpen(false); }} className="p-2 hover:bg-white/20 rounded-lg min-w-[40px] min-h-[40px] flex items-center justify-center">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <ChatMessages messages={messages} endRef={endRef} />
          <VoiceStatus voice={voice} speaking={speaking} onStop={stopSpeaking} />
          <ChatComposer input={input} setInput={setInput} send={send} busy={busy} voice={voice} />
        </div>
      )}
    </>
  );
}
